// Google Play Billing wrapper for OpenCSV's Capacitor Android app.
//
// Plugin: cordova-plugin-purchase v13 (CdvPurchase). It auto-registers as a
// global `CdvPurchase` on Capacitor Android. We only ever load/init it when
// running inside the native Android shell — on web/iOS this module is a no-op
// so the existing Stripe checkout flow keeps working unchanged.
//
// One product: `opencsv_lifetime` (non-consumable, $0.99). After a successful
// approval, we hand the token off to our backend (/api/billing/google-play/
// verify) which talks to Google Play's Developer API server-side. The backend
// is the only thing that flips `is_paid=true` — the client never trusts itself.

import { api } from "./api";
import { getNativePlatform } from "./platform";

const PRODUCT_ID = "opencsv_lifetime";

let _initPromise = null;
let _store = null;
let _registeredProductId = null;

// Status callbacks the UI can subscribe to. The plugin is event-driven so we
// translate its callbacks into a single listener model the React component can
// consume.
const _listeners = new Set();
function _emit(evt) {
    for (const fn of _listeners) {
        try { fn(evt); } catch { /* ignore listener errors */ }
    }
}

export function onBillingEvent(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
}

export function isAndroidPlatform() {
    return getNativePlatform() === "android";
}

/**
 * Lazy-initialise the CdvPurchase store. Safe to call multiple times — the
 * second-onwards call returns the same in-flight promise.
 */
export function initBilling() {
    if (_initPromise) return _initPromise;
    if (!isAndroidPlatform()) {
        _initPromise = Promise.resolve(null);
        return _initPromise;
    }
    _initPromise = new Promise((resolve, reject) => {
        const start = () => {
            try {
                const CdvPurchase = window.CdvPurchase;
                if (!CdvPurchase || !CdvPurchase.store) {
                    reject(new Error("CdvPurchase plugin not available"));
                    return;
                }
                _store = CdvPurchase.store;
                // Verbose logs help when reading `adb logcat`.
                _store.verbosity = CdvPurchase.LogLevel.WARNING;

                // Register the single non-consumable product.
                if (_registeredProductId !== PRODUCT_ID) {
                    _store.register([{
                        id: PRODUCT_ID,
                        type: CdvPurchase.ProductType.NON_CONSUMABLE,
                        platform: CdvPurchase.Platform.GOOGLE_PLAY,
                    }]);
                    _registeredProductId = PRODUCT_ID;
                }

                // Wire purchase lifecycle events.
                _store.when()
                    .productUpdated((p) => _emit({ type: "product", product: _snapshotProduct(p) }))
                    .approved(async (transaction) => {
                        _emit({ type: "approved", transaction });
                        await _verifyWithBackendAndFinish(transaction);
                    })
                    .finished((transaction) => _emit({ type: "finished", transaction }));

                _store.error((err) => {
                    _emit({ type: "error", error: err });
                });

                _store.initialize([CdvPurchase.Platform.GOOGLE_PLAY])
                    .then(() => resolve(_store))
                    .catch((e) => reject(e));
            } catch (e) {
                reject(e);
            }
        };
        // The plugin registers itself on `deviceready` (cordova-style). When
        // the Capacitor webview is loaded after that event has already fired,
        // CdvPurchase is just there. Otherwise we wait for it.
        if (window.CdvPurchase?.store) {
            start();
        } else {
            document.addEventListener("deviceready", start, { once: true });
            // Safety timeout — if deviceready never fires, fail loudly.
            setTimeout(() => {
                if (!_store) reject(new Error("Billing plugin did not initialise (deviceready timeout)"));
            }, 8000);
        }
    });
    return _initPromise;
}

function _snapshotProduct(p) {
    if (!p) return null;
    // CdvPurchase v13 surfaces the price via the first `offer`'s pricingPhase.
    const offer = (p.offers && p.offers[0]) || null;
    const pricing = offer?.pricingPhases?.[0] || null;
    return {
        id: p.id,
        title: p.title,
        description: p.description,
        priceString: pricing?.price || offer?.pricing?.price || null,
        priceMicros: pricing?.priceMicros || null,
        currency: pricing?.currency || null,
        owned: p.owned === true,
    };
}

export function getLifetimeProduct() {
    if (!_store) return null;
    try {
        const CdvPurchase = window.CdvPurchase;
        const product = _store.get(PRODUCT_ID, CdvPurchase.Platform.GOOGLE_PLAY);
        return _snapshotProduct(product);
    } catch { return null; }
}

/** Kick off the Play Billing purchase flow. */
export async function buyLifetime() {
    if (!isAndroidPlatform()) throw new Error("Play Billing only available on Android");
    await initBilling();
    const CdvPurchase = window.CdvPurchase;
    const product = _store?.get(PRODUCT_ID, CdvPurchase.Platform.GOOGLE_PLAY);
    if (!product) throw new Error("Product unavailable. Try again in a moment.");
    const offer = product.getOffer ? product.getOffer() : (product.offers && product.offers[0]);
    if (!offer) throw new Error("No purchase offer found for this product.");
    return _store.order(offer);
}

/** Manually trigger a Play Billing "restore purchases" sync. */
export async function restorePurchases() {
    if (!isAndroidPlatform()) return;
    await initBilling();
    if (_store?.restorePurchases) {
        await _store.restorePurchases();
    }
}

async function _verifyWithBackendAndFinish(transaction) {
    // The transaction object carries the Google purchase token + product list.
    try {
        const token = transaction?.transactionId || transaction?.purchaseToken;
        const products = transaction?.products || [];
        const productId = products[0]?.id || products[0]?.productId || PRODUCT_ID;
        if (!token) {
            _emit({ type: "error", error: new Error("Missing purchaseToken on approved transaction") });
            return;
        }
        const { data } = await api.post("/billing/google-play/verify", {
            product_id: productId,
            purchase_token: token,
            package_name: "io.opencsv.app",
        });
        _emit({ type: "verified", result: data });
        // Acknowledge with the store so it doesn't keep replaying `approved`.
        if (typeof transaction.finish === "function") {
            await transaction.finish();
        }
    } catch (e) {
        const detail = e?.response?.data?.detail || e?.message || "Verification failed";
        _emit({ type: "error", error: new Error(String(detail)) });
    }
}
